import { ReportData } from '../types/reportTypes';

// Parse currency string to number
const parseCurrency = (value: string): number => {
  if (!value || value === '') return 0;
  return parseFloat(value.replace(/[$,]/g, ''));
};

// Parse percentage string to number
const parsePercentage = (value: string): number => {
  if (!value || value === '') return 0;
  return parseFloat(value.replace(/%/g, '')) / 100;
};

// Calculate the sum of an array of numbers
const sum = (numbers: number[]): number => {
  return numbers.reduce((total, num) => total + num, 0);
};

// Calculate net profit and commission for Cody Cline reports
const calculateCodyNetProfit = (grossProfit: number, companyOH: number): number => {
  // For Cody Cline: Net Profit = Gross Profit - Company OH
  return grossProfit - companyOH;
};

const calculateCodyCommission = (netProfit: number): number => {
  // For Cody Cline: Sales Commission = 50% of Net Profit
  return netProfit / 2;
};

// Calculate the individual job data from the Cody Sales February CSV
// IMPORTANT: Using 13% flat OH calculation (Contract Total × 0.13) retroactive to 1/1/2025
const jobData = [
  {
    jobInfo: "230: Rhiannon Jensen",
    leadSource: "Referral- Cody's network",
    trade: "Windows & Doors",
    contractTotal: 40000.00,
    grossMarginPct: 0.3985,
    grossProfit: 15940.00,
    salesCommission: 0,
    salesManagerFee: 0,
    companyOH: 40000.00 * 0.13, // 13% flat rate
    netProfit: 0
  },
  {
    jobInfo: "229: Linda & Doug Sanborn",
    leadSource: "Referral- Dominic's network",
    trade: "Roofing",
    contractTotal: 25416.05,
    grossMarginPct: 0.4216,
    grossProfit: 10715.41,
    salesCommission: 0,
    salesManagerFee: 0,
    companyOH: 25416.05 * 0.13, // 13% flat rate
    netProfit: 0
  },
  {
    jobInfo: "226: Virginia Kibler",
    leadSource: "Referral- Cody's network",
    trade: "Roofing",
    contractTotal: 9295.00,
    grossMarginPct: 0.4005,
    grossProfit: 3722.65,
    salesCommission: 0,
    salesManagerFee: 0,
    companyOH: 9295.00 * 0.13, // 13% flat rate
    netProfit: 0
  },
  {
    jobInfo: "217: Marlon & Christi Cox",
    leadSource: "Referral- Dominic's network",
    trade: "Roofing",
    contractTotal: 20715.45,
    grossMarginPct: 0.4513,
    grossProfit: 9348.88,
    salesCommission: 0,
    salesManagerFee: 0,
    companyOH: 20715.45 * 0.13, // 13% flat rate
    netProfit: 0
  },
  {
    jobInfo: "238: Chris Meckel Binkley-Chalk Project",
    leadSource: "Referral- Cody's network",
    trade: "Siding",
    contractTotal: 46017.50,
    grossMarginPct: 0.4000,
    grossProfit: 18407.00, // 46017.50 * 0.40
    salesCommission: 0,
    salesManagerFee: 0,
    companyOH: 46017.50 * 0.13, // 13% flat rate
    netProfit: 0
  },
  {
    jobInfo: "214: Peoples Congregational Church",
    leadSource: "Referral- Dominic's network",
    trade: "Roofing",
    contractTotal: 2480.00,
    grossMarginPct: 0.4000,
    grossProfit: 992.00, // 2480.00 * 0.40
    salesCommission: 0,
    salesManagerFee: 0,
    companyOH: 2480.00 * 0.13, // 13% flat rate
    netProfit: 0
  },
  {
    jobInfo: "234: Kenneth Johnson",
    leadSource: "Referral- Cody's network",
    trade: "Gutters",
    contractTotal: 215.00,
    grossMarginPct: 0.0698,
    grossProfit: 15.01,
    salesCommission: 0,
    salesManagerFee: 0,
    companyOH: 215.00 * 0.13, // 13% flat rate
    netProfit: 0
  },
  {
    jobInfo: "232: Thornhill Properties",
    leadSource: "Referral- Cody's network",
    trade: "Roofing",
    contractTotal: 12215.50,
    grossMarginPct: 0.5098,
    grossProfit: 6227.46,
    salesCommission: 0,
    salesManagerFee: 0,
    companyOH: 12215.50 * 0.13, // 13% flat rate
    netProfit: 0
  },
  {
    jobInfo: "231: Loretta Powers",
    leadSource: "Referral- Dominic's network",
    trade: "Roofing",
    contractTotal: 5989.00,
    grossMarginPct: 0.3713,
    grossProfit: 2223.72,
    salesCommission: 0,
    salesManagerFee: 0,
    companyOH: 5989.00 * 0.13, // 13% flat rate
    netProfit: 0
  },
  {
    jobInfo: "227: Ballard Green Condo 1",
    leadSource: "Referral- Dominic's network",
    trade: "Roofing",
    contractTotal: 899.00,
    grossMarginPct: 0.5551,
    grossProfit: 499.03,
    salesCommission: 0,
    salesManagerFee: 0,
    companyOH: 899.00 * 0.13, // 13% flat rate
    netProfit: 0
  },
  {
    jobInfo: "235: Ellicott Hills Condo 1",
    leadSource: "Referral- Dominic's network",
    trade: "Other",
    contractTotal: 20965.00,
    grossMarginPct: 0.5497,
    grossProfit: 11524.46,
    salesCommission: 0,
    salesManagerFee: 0,
    companyOH: 20965.00 * 0.13, // 13% flat rate
    netProfit: 0
  },
  {
    jobInfo: "228: Ballard Green Condo 4",
    leadSource: "Referral- Dominic's network",
    trade: "Roofing",
    contractTotal: 2999.00,
    grossMarginPct: 0.5332,
    grossProfit: 1599.07,
    salesCommission: 0,
    salesManagerFee: 0,
    companyOH: 2999.00 * 0.13, // 13% flat rate
    netProfit: 0
  }
];

// Apply Cody's calculation formula to each job
jobData.forEach(job => {
  job.netProfit = calculateCodyNetProfit(job.grossProfit, job.companyOH);
  job.salesCommission = calculateCodyCommission(job.netProfit);
});

// Calculate sales by source - matching the format needed for charts
const salesBySource = (() => {
  const sourceMap = new Map<string, any>();
  
  jobData.forEach(job => {
    const key = job.leadSource;
    if (!sourceMap.has(key)) {
      sourceMap.set(key, {
        source: key,
        count: 0,
        totalSales: 0,
        grossProfit: 0
      });
    }
    
    const sourceData = sourceMap.get(key);
    sourceData.count += 1;
    sourceData.totalSales += job.contractTotal;
    sourceData.grossProfit += job.grossProfit;
  });
  
  return Array.from(sourceMap.values());
})();

// Create ad spend by source data - all sources have 0 spend for February
const adSpendBySource = (() => {
  const uniqueSources = new Set<string>();
  jobData.forEach(job => uniqueSources.add(job.leadSource));
  
  return Array.from(uniqueSources).map(source => ({
    source: source,
    spend: 0
  }));
})();

// Calculate sales by trade
const salesByTrade = (() => {
  const tradeMap = new Map<string, any>();
  
  jobData.forEach(job => {
    const key = job.trade;
    const normalizedKey = key === "Siding" ? "Siding" : 
                         key === "Roofing" ? "Roofing" : 
                         key === "Windows & Doors" ? "Windows & Doors" : 
                         key === "Gutters" ? "Other" :
                         "Other";
    
    if (!tradeMap.has(normalizedKey)) {
      tradeMap.set(normalizedKey, {
        trade: normalizedKey,
        count: 0,
        totalSales: 0,
        grossProfit: 0,
        avgContractValue: 0,
        avgGrossMargin: 0
      });
    }
    
    const tradeData = tradeMap.get(normalizedKey);
    tradeData.count += 1;
    tradeData.totalSales += job.contractTotal;
    tradeData.grossProfit += job.grossProfit;
  });
  
  // Calculate averages
  tradeMap.forEach(trade => {
    trade.avgContractValue = trade.count > 0 ? trade.totalSales / trade.count : 0;
    trade.avgGrossMargin = trade.totalSales > 0 ? trade.grossProfit / trade.totalSales : 0;
  });
  
  return Array.from(tradeMap.values());
})();

// Calculate totals
const totalJobs = jobData.length;
const totalSales = sum(jobData.map(job => job.contractTotal));
const totalGrossProfit = sum(jobData.map(job => job.grossProfit));
const totalCompanyOH = sum(jobData.map(job => job.companyOH));
const totalNetProfit = sum(jobData.map(job => job.netProfit));
const totalSalesCommissions = sum(jobData.map(job => job.salesCommission));
const totalManagerFees = 0; // No manager fees for Cody
const totalAdSpend = 0; // No ad spend for February based on CSV

// Export the full report data for February 2025
export const codyFebruaryData: ReportData = {
  period: {
    month: 2,
    year: 2025
  },
  kpis: {
    adSpend: totalAdSpend,
    totalSales,
    grossProfit: totalGrossProfit,
    salesCommissions: totalSalesCommissions,
    managerFees: totalManagerFees,
    companyOH: totalCompanyOH,
    netProfit: totalNetProfit,
    netProfitPercent: totalSales > 0 ? totalNetProfit / totalSales : 0,
    grossProfitPercent: totalSales > 0 ? totalGrossProfit / totalSales : 0
  },
  salesBySource,
  salesByTrade,
  adSpendBySource,
  jobData
};